'use strict';
/**
 * Tank Design Studio - the menu-only 3D skin picker. Shows the same "Rookie"
 * imported GLB tank as the start-screen mascot (menu-tank.js), but orbit-
 * controllable by the player and recolored live from the Settings color
 * palette (TANK_COLORS in client.js).
 *
 * Fully decoupled from client.js (a classic script, not a module): this file
 * watches #start-screen's class list for '.studio-open' instead of being
 * called into directly - the same idiom menu-tank.js already uses for
 * '.screen-off' - and reacts to color picks via a 'tanks:colorchange'
 * CustomEvent client.js dispatches, rather than either file importing the
 * other.
 */
import * as THREE from 'three';
import { OrbitControls } from './vendor/OrbitControls.js';
import { buildTank as buildRookieTank } from './tank-concepts/tank-08-rookie.js';

// Mirrors TANK_COLORS in client.js (itself a mirror of server/index.js's
// COLORS) - duplicated here, same trade-off client.js's own copy already
// documents, so this fully independent module can turn a persisted swatch
// index into a hex without importing the non-module client.js.
const TANK_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f1c40f', '#9b59b6',
  '#e67e22', '#1abc9c', '#ff6fa5', '#00d2ff', '#c0392b',
];
const STORAGE_TANK_COLOR = 'panzer_tank_color';

const startScreen = document.getElementById('start-screen');
const container = document.getElementById('tank-studio-stage');
const canvas = document.getElementById('tank-studio-canvas');

let scene, camera, renderer, controls, tank, resizeObserver;
let rafId = null;
let lastT = 0;
let revealed = false;
let building = null; // in-flight build promise, so rapid open/close/open doesn't double-init
let built = false;

const pointer = { x: 0, y: 0 };
window.addEventListener('pointermove', (e) => {
  pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
  pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
}, { passive: true });

function applyColorIndex(index) {
  // null = "random" preference (server assigns the color) - nothing sensible
  // to preview, so just leave whatever color is already showing.
  if (!tank || index == null) return;
  const hex = TANK_COLORS[index];
  if (!hex) return;
  tank.skinMaterials.body.forEach((m) => m.color.set(hex));
}

window.addEventListener('tanks:colorchange', (e) => {
  applyColorIndex(e.detail && e.detail.index);
});

async function buildScene() {
  renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;

  scene = new THREE.Scene();

  // same bright toy-diorama lighting rig as menu-tank.js / tank-gallery.js,
  // so material/color choices read the same way here as everywhere else.
  scene.add(new THREE.HemisphereLight(0xcfe0ff, 0x2a1f4a, 1.05));
  const key = new THREE.DirectionalLight(0xfff2d6, 1.6);
  key.position.set(4, 6.5, 5);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x8fb2ff, 0.55);
  rim.position.set(-5, 3, -4);
  scene.add(rim);
  const fill = new THREE.PointLight(0xffffff, 0.35);
  fill.position.set(-2.5, 1.2, 3);
  scene.add(fill);

  // no driveMode - the tank shouldn't wander off-camera while a player is
  // orbiting it by hand, just its normal idle sway.
  tank = await buildRookieTank({});
  scene.add(tank.root);
  applyPersistedColor();

  // camera: bounding-box auto-fit, same approach as tank-gallery.js's
  // per-card viewport.
  const box = new THREE.Box3().setFromObject(tank.root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.length() * 0.5, 0.5);

  const fov = 32;
  camera = new THREE.PerspectiveCamera(fov, 4 / 3, 0.05, 200);
  const dist = (radius / Math.sin(THREE.MathUtils.degToRad(fov / 2))) * 1.15;
  const dir = new THREE.Vector3(0.6, 0.5, 1).normalize();
  camera.position.copy(center).addScaledVector(dir, dist);
  camera.lookAt(center);

  controls = new OrbitControls(camera, canvas);
  controls.target.copy(center);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = dist * 0.4;
  controls.maxDistance = dist * 2.8;
  controls.minPolarAngle = 0.2;
  controls.maxPolarAngle = 1.45;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 3.2;
  controls.update();

  let resumeTimer = null;
  controls.addEventListener('start', () => {
    controls.autoRotate = false;
    if (resumeTimer) clearTimeout(resumeTimer);
  });
  controls.addEventListener('end', () => {
    resumeTimer = setTimeout(() => { controls.autoRotate = true; }, 2500);
  });

  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
  } else {
    window.addEventListener('resize', resize);
  }
  resize();
}

function applyPersistedColor() {
  let index;
  try { index = JSON.parse(localStorage.getItem(STORAGE_TANK_COLOR)); } catch (e) { return; }
  applyColorIndex(index);
}

function frame(now) {
  rafId = requestAnimationFrame(frame);
  const dt = Math.min((now - lastT) / 1000, 0.05);
  lastT = now;
  if (document.hidden) return;
  tank.update(dt, pointer);
  controls.update();
  renderer.render(scene, camera);
  if (!revealed) {
    revealed = true;
    canvas.classList.add('ready');
  }
}

function startLoop() {
  if (rafId !== null) return;
  lastT = performance.now();
  rafId = requestAnimationFrame(frame);
}
function stopLoop() {
  if (rafId === null) return;
  cancelAnimationFrame(rafId);
  rafId = null;
}

async function ensureBuilt() {
  if (built) return;
  if (!building) building = buildScene().then(() => { built = true; });
  await building;
}

function isStudioOpen() {
  return startScreen.classList.contains('studio-open');
}

function onVisibilityChange() {
  if (isStudioOpen()) {
    ensureBuilt().then(startLoop).catch((err) => {
      // Decorative only - never break the menu if WebGL/three.js misbehaves.
      console.warn('[tank-studio] disabled:', err);
    });
  } else {
    stopLoop();
  }
}

if (startScreen && container && canvas) {
  // Lazy: the scene (GLB fetch + WebGL context) is only ever built the first
  // time '.studio-open' actually appears, so players who never open the
  // studio never pay for a second live WebGL context on top of the mascot's.
  new MutationObserver(onVisibilityChange).observe(startScreen, {
    attributes: true, attributeFilter: ['class'],
  });
  if (isStudioOpen()) onVisibilityChange();
}
